import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "events-and-observers"
    }}>{`Events and observers`}</h1>
    <p>{`Working with events and observers is one of the main ways to extend Adobe Commerce and Magento Open Source functionality. The events and observers implementation in Adobe Commerce and Magento Open Source are based on the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Publish%E2%80%93subscribe_pattern"
      }}>{`publish-subscribe pattern`}</a>{`. Using events and observers, you can run your custom code in response to a specific event or even a custom event.`}</p>
    <p>{`Adobe Commerce developers can use the following event-driven technologies to create the next generation of apps and extensions:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://developer.adobe.com/commerce/extensibility/events/"
          }}>{`Adobe I/O Events for Adobe Commerce`}</a>{` allows you to send events asynchronously to applications built with `}<a parentName="p" {...{
            "href": "https://developer.adobe.com/app-builder/docs/getting_started/first_app/"
          }}>{`Adobe App Builder`}</a>{`. This feature is available on Adobe Commerce 2.4.4 and higher.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://developer.adobe.com/commerce/extensibility/webhooks/"
          }}>{`Adobe Commerce Webhooks`}</a>{` enables you to synchronously send webhook calls to an external server for immediate processing. Webhooks are triggered by events and cancel the action that initiated the event if the external call does not succeed. This feature is available as of Adobe Commerce 2.4.4 and higher.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "events"
    }}>{`Events`}</h2>
    <p>{`Events are dispatched by modules when certain actions are triggered. In addition to its own events, the application allows you to create your own events that can be dispatched in your code. When an event is dispatched, it can pass data to any observers configured to watch that event.`}</p>
    <h3 {...{
      "id": "dispatching-events"
    }}>{`Dispatching events`}</h3>
    <p>{`Events can be dispatched using the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Event/ManagerInterface.php"
      }}><inlineCode parentName="a">{`Magento\\Framework\\Event\\ManagerInterface`}</inlineCode></a>{` class. This class can be obtained through `}<a parentName="p" {...{
        "href": "../dependency-injection.md"
      }}>{`dependency injection`}</a>{` by defining the dependency in your constructor.`}</p>
    <p>{`To dispatch an event, call the `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` function of the event manager class and provide it with the name of the event you want to dispatch along with an array of data you wish to provide to observers.`}</p>
    <p>{`The following example shows you how to dispatch an event with and without an array of data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace MyCompany\\MyModule;

use Magento\\Framework\\Event\\ManagerInterface as EventManager;

class MyClass
{
    /**
     * @var EventManager
     */
    private $eventManager;

    /*
     * @param EventManager $eventManager
     */
    public function __construct(EventManager $eventManager)
    {
        $this->eventManager = $eventManager;
    }

    public function something()
    {
        $eventData = null;
        // Code...
        $this->eventManager->dispatch('my_module_event_before');
        // More code that sets $eventData...
        $this->eventManager->dispatch('my_module_event_after', ['myEventData' => $eventData]);
    }
}

`}</code></pre>
    <h3 {...{
      "id": "creating-new-events"
    }}>{`Creating new events`}</h3>
    <p>{`Custom events can be dispatched by simply passing in a unique event name to the event manager when you call the `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` function. Your unique event name is referenced in your module's `}<inlineCode parentName="p">{`events.xml`}</inlineCode>{` file where you specify which observers will react to that event.`}</p>
    <p>{`You can make the custom event `}<inlineCode parentName="p">{`my_module_event_after`}</inlineCode>{` subscribable by declaring the `}<inlineCode parentName="p">{`MyCompany/MyModule/etc/events.xml`}</inlineCode>{` file as follows:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="urn:magento:framework:Event/etc/events.xsd">
    <event name="my_module_event_after">
        <observer name="my_module_event_after_observer" instance="MyCompany\\MyModule\\Observer\\MyEvent"/>
    </event>
</config>
`}</code></pre>
    <h3 {...{
      "id": "event-areas"
    }}>{`Event areas`}</h3>
    <p>{`Generally, the location of the `}<inlineCode parentName="p">{`events.xml`}</inlineCode>{` file will be under the `}<inlineCode parentName="p">{`<module-root>/etc`}</inlineCode>{` directory. Observers that are associated with events here will watch for these events globally. The `}<inlineCode parentName="p">{`events.xml`}</inlineCode>{` file can also be defined under the `}<inlineCode parentName="p">{`<module-root>/etc/frontend`}</inlineCode>{` and `}<inlineCode parentName="p">{`<module-root>/etc/adminhtml`}</inlineCode>{` directories to configure observers to only watch for events in those specific areas.`}</p>
    <p>{`Declare the observer in the appropriate area. The `}<inlineCode parentName="p">{`global`}</inlineCode>{` area allows the observer to run in all areas (`}<inlineCode parentName="p">{`adminhtml`}</inlineCode>{`, `}<inlineCode parentName="p">{`crontab`}</inlineCode>{`, `}<inlineCode parentName="p">{`frontend`}</inlineCode>{`, `}<inlineCode parentName="p">{`graphql`}</inlineCode>{`, `}<inlineCode parentName="p">{`webapi_rest`}</inlineCode>{`, `}<inlineCode parentName="p">{`webapi_soap`}</inlineCode>{`).`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Area`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`File location`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`global`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/events.xml`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`adminhtml`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/adminhtml/events.xml`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`crontab`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/crontab/events.xml`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`frontend`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/frontend/events.xml`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`graphql`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/graphql/events.xml`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`webapi_rest`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/webapi_rest/events.xml`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`webapi_soap`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<module-dir>/etc/webapi_soap/events.xml`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "observers"
    }}>{`Observers`}</h2>
    <p>{`Observers are a certain type of class that can influence general behavior, performance, or change business logic. Observers are executed whenever the event they are configured to watch is dispatched by the event manager.`}</p>
    <h3 {...{
      "id": "creating-an-observer"
    }}>{`Creating an observer`}</h3>
    <p>{`To create an observer, you must place your class file under your `}<inlineCode parentName="p">{`<module-root>/Observer`}</inlineCode>{` directory. Your observer class should implement `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Event/ObserverInterface.php"
      }}><inlineCode parentName="a">{`Magento\\Framework\\Event\\ObserverInterface`}</inlineCode></a>{` and define its `}<inlineCode parentName="p">{`execute`}</inlineCode>{` function.`}</p>
    <p>{`Below is an example of the basic observer class structure:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace MyCompany\\MyModule\\Observer;

use Magento\\Framework\\Event\\Observer;
use Magento\\Framework\\Event\\ObserverInterface;

class MyObserver implements ObserverInterface
{
    public function __construct()
    {
        // Observer initialization code...
        // You can use dependency injection to get any class this observer may need.
    }

    public function execute(Observer $observer)
    {
        // Observer execution code...
    }
}
`}</code></pre>
    <p>{`One of the more powerful features of observers is that they are able to use parameters passed into the event when it was dispatched. Below is an example of an observer obtaining data passed in when the event was dispatched.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace MyCompany\\MyModule\\Observer;

use Magento\\Framework\\Event\\Observer;
use Magento\\Framework\\Event\\ObserverInterface;

class AnotherObserver implements ObserverInterface
{
    public function __construct()
    {
        // Observer initialization code...
        // You can use dependency injection to get any class this observer may need.
    }

    public function execute(Observer $observer)
    {
        $myEventData = $observer->getData('myEventData');
        // Additional observer execution code...
    }
}
`}</code></pre>
    <h3 {...{
      "id": "subscribing-to-events"
    }}>{`Subscribing to events`}</h3>
    <p>{`Observers can be configured to watch certain events in the `}<inlineCode parentName="p">{`events.xml`}</inlineCode>{` file.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`observer`}</inlineCode>{` xml element has the following properties:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`name`}</inlineCode>{` (required) - The name of the observer for the event definition.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`instance`}</inlineCode>{` (required) - The fully qualified class name of the observer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`disabled`}</inlineCode>{` - Determines whether this observer is active or not. Default value is false.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`shared`}</inlineCode>{` - Determines the `}<a parentName="li" {...{
          "href": "../../build/dependency-injection-file.md#object-lifestyle-configuration"
        }}>{`lifestyle`}</a>{` of the class. Default is `}<inlineCode parentName="li">{`true`}</inlineCode>{`.`}</li>
    </ul>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`The observer name must be unique, or an override will occur.`}</p>
    <p>{`Below is an example of how to assign observers to watch certain events:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0"?>
<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="urn:magento:framework:Event/etc/events.xsd">
    <event name="my_module_event_before">
        <observer name="myObserverName" instance="MyCompany\\MyModule\\Observer\\MyObserver" />
    </event>
    <event name="my_module_event_after">
        <observer name="myObserverName" instance="MyCompany\\MyModule\\Observer\\AnotherObserver" />
    </event>
</config>
`}</code></pre>
    <p>{`In the preceding example, we assign the observer `}<inlineCode parentName="p">{`MyObserver`}</inlineCode>{` to the custom event `}<inlineCode parentName="p">{`my_module_event_before`}</inlineCode>{` and `}<inlineCode parentName="p">{`AnotherObserver`}</inlineCode>{` to `}<inlineCode parentName="p">{`my_module_event_after`}</inlineCode>{`.`}</p>
    <p>{`Observer names must be unique per event definition. This means that you cannot have two observers with the same name in the same event definition. In the example, both observers have the name `}<inlineCode parentName="p">{`myObserverName`}</inlineCode>{`. This is acceptable because each of those observers belong to different event definitions.`}</p>
    <p>{`If you declare an observer with a name that is already in use within the same event, the application merges these declaration nodes into a single observer declaration, respecting the module load order as defined in the `}<inlineCode parentName="p">{`app/etc/config.php`}</inlineCode>{` file. This is useful when disabling an observer declared in another module.`}</p>
    <h3 {...{
      "id": "disabling-an-observer"
    }}>{`Disabling an observer`}</h3>
    <p>{`Existing observers can be disabled, if you do not want to have them running. It is a good practice to disable the observer if you want to change its logic rather than override it.
Below is an example of how to disable the previously created observer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0"?>
<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="urn:magento:framework:Event/etc/events.xsd">
    <event name="my_module_event_before">
        <observer name="myObserverName" disabled="true" />
    </event>
</config>
`}</code></pre>
    <h2 {...{
      "id": "recommended-reading"
    }}>{`Recommended Reading`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/commerce/php/best-practices/extensions/observers/"
        }}>{`Observers best practices`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      